import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Modal } from 'antd';
import style from './index.scss';
import { EVENT_CATEGORY } from './constants';

/**
 * "Add new activity type" / "Add new transaction type".
 *
 * Two fields, and the second one is the whole point: `technicalValue` is matched
 * against the client payload EXACTLY. Getting it wrong does not raise anything —
 * the type saves, its fields save, and then no event ever matches it. Hence the
 * mono treatment and the duplicate check before the modal will close.
 *
 * ── THE TECHNICAL VALUE IS EDITABLE ON EDIT, AND THE EDIT PERSISTS ─────────
 *
 * The design's Edit dialog shows the input enabled, so a wrong value can be
 * corrected in place. (Disabling it on 2026-09-02, on a misreading of §15, was
 * deliberately reverted — do not disable it again.)
 *
 * BE commit 7d13a65ce2 briefly made the edit a silent no-op by dropping
 * `technicalValue` from `EventTypeUpdateRequest`; FIN-13930 restored the write
 * (optional on the request, re-checked for uniqueness, refused on the system
 * default type).
 *
 * ── OPEN: THE DERIVATION DISAGREES WITH REAL DATA ──────────────────────────
 *
 * This comment used to read "NO AUTO-DERIVATION FROM THE DISPLAY NAME,
 * deliberately", and gave the reason: real pipelines send UPPERCASE, so a
 * lowercase guess "would quietly produce a value that matches nothing, and the
 * admin would have no reason to doubt it."
 *
 * The code below now derives anyway — added when the design was seen to show a
 * suggested value, WITHOUT reconciling it against the warning above it. Both
 * cannot be right, so the warning is recorded here rather than left to imply an
 * intent the code no longer has.
 *
 * The warning was correct. Measured against local data on 2026-09-07:
 *   every activity_type in StarRocks is uppercase
 *     LOGIN · LOGIN_MFA_WITH_OTP · PASSWORD_CHANGED ·
 *     ACCOUNT_PASSWORD_RESET · MODIFY_TRANSACTION_SETTINGS
 *   and the one row created THROUGH THIS DIALOG is `new_activity`, lowercase,
 *   matching no event at all — while holding four configured fields and
 *   is_default = 1, i.e. it became the category fallback.
 * Case is not the whole problem either: the seeded transaction type is
 * `XBORDER` for "Cross Border Remittance", which no derivation could produce.
 *
 * THREE OPTIONS, for Yogesh:
 *   1. keep filling the field (today) — asserts a value that is usually wrong;
 *   2. derive in UPPERCASE — fixes LOGIN and PASSWORD_CHANGED, not XBORDER, and
 *      makes the remaining wrong guesses look more authoritative;
 *   3. derive into the PLACEHOLDER only, which is what the design shows — a
 *      suggestion the admin must accept by typing, so nothing is asserted.
 * (3) is the recommendation; it needs one decision first — whether an untouched
 * field submits the suggestion or is rejected. The submit is currently gated on
 * both fields having content, so today it would be rejected.
 *
 * NOTHING ON SCREEN STATES ANY OF THIS. The field's hint used to end "...must
 * match the value in your payload exactly, including case" and was removed on
 * 2026-09-07 to match the design, which shows no hint.
 */
/**
 * The suggested `activity_type` / `txn_type` for a display name.
 *
 * The design previews one as you type — "new activity" becomes `new_activity`
 * (checked 2026-09-02) — so the admin is not left guessing the shape of a value
 * they cannot change later.
 *
 * SNAKE_CASE, LOWER, matching what the design shows. Note that every value in
 * real payloads so far is UPPERCASE (LOGIN, PASSWORD_CHANGED, XBORDER), so this
 * is a starting point rather than an answer — which is why it is only a
 * SUGGESTION and the field stays editable.
 *
 * THE CASE CAVEAT IS NO LONGER STATED ANYWHERE IN THE UI. The field's hint used
 * to carry it and was removed on 2026-09-07 to match the design. The mismatch
 * it warned about is still real — a lowercase suggestion against uppercase
 * payload values — so it is worth confirming with Yogesh whether the derivation
 * should uppercase instead, now that nothing on screen explains the difference.
 */
const suggestTechnicalValue = (displayName) =>
  (displayName || '')
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

const AddEventTypeModal = ({
  open,
  category,
  existingValues,
  editing,
  onAdd,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  /* Once the admin types in the technical field themselves, stop deriving it
     from the display name — otherwise renaming the label would silently rewrite
     a value they had chosen deliberately. Reset with the modal, since
     `destroyOnClose` gives every open a fresh form. */
  const [technicalTouched, setTechnicalTouched] = useState(false);

  /* WHY WATCH RATHER THAN VALIDATE: the design shows no asterisks and no red
     "required" text, but a type with no name or no technical value must still
     be impossible to create. Disabling the action until both fields have
     content enforces that WITHOUT ever entering an error state — the admin is
     stopped before the form complains rather than after.
     The uniqueness error is untouched and still shows in red: that one is a
     genuine conflict the admin has to be told about, not a nag about an empty
     box they have not reached yet. */
  const watchedName = Form.useWatch('displayName', form);
  const watchedValue = Form.useWatch('technicalValue', form);
  const incomplete = !watchedName?.trim() || !watchedValue?.trim();

  const isActivity = category === EVENT_CATEGORY.ACTIVITY;
  const keyName = isActivity ? 'activity_type' : 'txn_type';

  // EDIT MODE reuses this modal rather than duplicating the form: the fields
  // are the same, so a second component would only drift from this one.
  //
  // BOTH FIELDS ARE EDITABLE. This comment used to say §15 allowed "editing the
  // display name only, so the difference is one disabled input" — and on
  // 2026-09-02 I implemented exactly that, disabling the technical value, which
  // was then deliberately reverted. The differences between the two modes are
  // copy (title, intro, button, no payload hint) and the uniqueness check
  // excluding the type's own value — not a disabled field.
  const isEdit = Boolean(editing);

  /**
   * PREFILL WHEN EDITING. The Edit modal opened EMPTY — the intent recorded at
   * the top of this file ("the difference is one disabled input") was never
   * implemented, so clicking the pencil on "Login" gave a blank form, and
   * saving it would have renamed the type to nothing. Found 2026-09-02 while
   * testing the derived technical value.
   *
   * Keyed on the modal opening rather than on `editing` alone: `destroyOnClose`
   * remounts the form each time, so the values have to be set after that.
   */
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      form.setFieldsValue({
        displayName: editing.displayName,
        technicalValue: editing.technicalValue
      });
    } else {
      form.resetFields();
    }
  }, [open, isEdit, editing, form]);

  const close = () => {
    form.resetFields();
    // `destroyOnClose` throws away the modal's CHILDREN, but this component
    // stays mounted — so the flag has to be cleared by hand or the next open
    // would refuse to derive.
    setTechnicalTouched(false);
    onCancel();
  };

  const submit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      onAdd({
        ...(isEdit ? { id: editing.id } : {}),
        displayName: values.displayName.trim(),
        technicalValue: values.technicalValue.trim()
      });
      form.resetFields();
      setTechnicalTouched(false);
    } catch (e) {
      // antd rejects on validation failure; the messages are already on the
      // fields, so there is nothing to add here.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      className={style.settingsModal}
      width={448}
      maskStyle={{ background: 'rgba(0, 0, 0, 0.8)' }}
      title={
        /* Generic on edit ("Edit activity type"), not the type's own name —
           the design's title does not repeat what the Display name field
           already shows. Checked against it 2026-09-03. */
        isEdit
          ? `Edit ${isActivity ? 'activity' : 'transaction'} type`
          : `Add new ${isActivity ? 'activity' : 'transaction'} type`
      }
      open={open}
      onCancel={close}
      onOk={submit}
      okText={isEdit ? 'Save changes' : 'Add type'}
      okButtonProps={{
        loading: submitting,
        disabled: incomplete,
        className: style.primaryButton
      }}
      destroyOnClose
    >
      {/* The intro was the ADD copy in both modes — the Edit dialog opened
          saying "Declare a NEW activity_type value" over a type that already
          exists (spotted 2026-09-03). */}
      <p className={style.modalIntro}>
        {isEdit ? (
          <>
            Correct the display name or the <code>{keyName}</code> value. Field
            and icon configuration is kept and moves with the new value.
          </>
        ) : (
          <>
            Declare a new <code>{keyName}</code> value. You can configure its
            fields and icon now; the configuration applies automatically once
            events of this type arrive.
          </>
        )}
      </p>

      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="displayName"
          label="Display name"
          /* NO ASTERISK, in either mode. It was `required={!isEdit}` on the
             belief that the design marked ADD and not EDIT; the design's ADD
             dialog has no asterisk either (checked 2026-09-04), so the earlier
             comment here was wrong.
             THE RULE STAYS as a backstop — it simply cannot fire in normal use,
             because the submit button is disabled until both fields have
             content. That is what keeps the red "required" text off the screen
             without letting a nameless type be created.
             `required={false}` IS NOT REDUNDANT: antd derives the asterisk from
             the RULES, so a `required: true` rule re-adds it unless the prop
             says otherwise. Dropping the prop alone left the asterisk exactly
             where it was. */
          required={false}
        >
          <Input
            placeholder="e.g. Device Binding"
            /* The BROWSER\'s own autofill, not ours. Chrome keys saved values on the
               input name, and antd\'s Form.Item passes name="displayName" straight
               through — so every display name ever submitted came back as a
               suggestion list floating over the modal. */
            autoComplete="off"
            autoFocus
            onChange={(e) => {
              /* Fill the technical value FROM the name, but only while the
                 admin has not typed one themselves — `touched` goes true on
                 their first keystroke there and we stop interfering. Editing an
                 existing type never derives: its technical value is matched
                 against live payloads, so silently rewriting it from a renamed
                 label would be wrong. */
              if (isEdit || technicalTouched) return;
              form.setFieldsValue({
                technicalValue: suggestTechnicalValue(e.target.value)
              });
            }}
          />
        </Form.Item>

        <Form.Item
          name="technicalValue"
          label={`Technical value (${keyName})`}
          /* No asterisk — see the Display name field above. */
          required={false}
          /* The old copy claimed the value "cannot be changed after the type
             is created", and it was the sentence I mistakenly implemented as a
             disabled input. On edit it now says what changing it actually does. */
          /* NO HINT, in either mode. It read "Must match the value in your
             payload exactly, including case." and was kept on ADD on the belief
             that the design showed it there; the design's ADD dialog has none
             either (checked 2026-09-07), so it is gone rather than mode-
             dependent.
             The rule it stated has NOT gone away — it is still true that the
             value must match the payload byte for byte. It is simply not said
             here any more. If it needs saying, it belongs somewhere that is not
             under an empty box on first open. */
          rules={[
            /* NO `required` RULE. The submit button is already disabled until
                 both fields have content (`incomplete` above), so this could only
                 ever fire on BLUR — a red error under a box the admin has not
                 filled in yet, on a form they cannot submit anyway. Removed
                 2026-09-09 alongside the display-name one, same reasoning as the
                 asterisks and red "required" labels dropped earlier.
                 The duplicate-value validator below STAYS: it reports a real
                 conflict the admin cannot otherwise see. */
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const v = value.trim();
                // §: "user should not be allowed to use the same technical name
                // multiple times". Checked here rather than only on save, so the
                // admin finds out before configuring a type that cannot be kept.
                // Case-INSENSITIVE: two types differing only by case would both
                // be unmatchable in practice and impossible to tell apart in the
                // list.
                //
                // RUNS ON EDIT TOO. It used to skip when editing, because the
                // field was disabled there — and the design leaves the input
                // enabled. The type's OWN current value is excluded, or every
                // edit would report a clash with itself. The BE re-checks on save
                // (409), so this is the early warning, not the guard.
                const own = isEdit
                  ? editing.technicalValue.toLowerCase()
                  : null;
                const clash = existingValues.some(
                  (existing) =>
                    existing.toLowerCase() === v.toLowerCase() &&
                    existing.toLowerCase() !== own
                );
                return clash
                  ? Promise.reject(
                      new Error(`${keyName} "${v}" is already configured`)
                    )
                  : Promise.resolve();
              }
            }
          ]}
        >
          <Input
            className={style.monoInput}
            autoComplete="off"
            /* `device_binding`, NOT `e.g. DEVICE_BINDING`. Two corrections from the
                 design (2026-09-09): no "e.g." prefix on this one, and lower
                 case. The case matters — `deriveTechnical` above lowercases,
                 so the old placeholder advertised a shape the field would
                 never auto-fill with, and PRD-2's own example is `device_bind`.
                 NOTE the live data disagrees: every configured type here is
                 UPPERCASE (LOGIN, ACCOUNT_PASSWORD_RESET, XBORDER). Design and
                 PRD say lower, ingestion says upper — still open with Yogesh.
                 The value must match the payload EXACTLY, so this is a real
                 question, not a cosmetic one. */
            placeholder="device_binding"
            onChange={() => setTechnicalTouched(true)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

AddEventTypeModal.propTypes = {
  open: PropTypes.bool,
  category: PropTypes.oneOf(Object.values(EVENT_CATEGORY)).isRequired,
  /** Technical values already configured in THIS category — the two categories
      are separate namespaces, so the same value may exist in both. */
  existingValues: PropTypes.arrayOf(PropTypes.string),
  /** Present in EDIT mode — the type being renamed. */
  editing: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    displayName: PropTypes.string,
    technicalValue: PropTypes.string
  }),
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

AddEventTypeModal.defaultProps = {
  open: false,
  existingValues: [],
  editing: undefined
};

export default AddEventTypeModal;
